import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { User } from '../types';

const poolConfig = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID!,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID!,
};

const userPool = new CognitoUserPool(poolConfig);

interface UserContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  idToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  logout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within a UserProvider');
  return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const applySession = useCallback((session: CognitoUserSession) => {
    const token = session.getIdToken();
    const payload = token.decodePayload();
    setIdToken(token.getJwtToken());
    setUser({
      cognito_sub: payload['sub'],
      email: payload['email'],
      created_at: new Date(payload['iat'] * 1000).toISOString(),
    });
  }, []);

  // Restore session on mount
  useEffect(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      setIsLoading(false);
      return;
    }
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) {
        setIsLoading(false);
        return;
      }
      applySession(session);
      setIsLoading(false);
    });
  }, [applySession]);

  const login = async (email: string, password: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      const authDetails = new AuthenticationDetails({ Username: email, Password: password });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          applySession(session);
          resolve();
        },
        onFailure: (err) => reject(err),
        newPasswordRequired: () => {
          reject(new Error('Password change required — please contact support'));
        },
      });
    });
  };

  const register = async (email: string, password: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const attributes = [
        new CognitoUserAttribute({ Name: 'email', Value: email }),
      ];
      userPool.signUp(email, password, attributes, [], (err) => {
        if (err) return reject(err);
        resolve();
      });
    });
  };

  const confirmSignUp = async (email: string, code: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.confirmRegistration(code, true, (err) => {
        if (err) return reject(err);
        resolve();
      });
    });
  };

  const logout = () => {
    userPool.getCurrentUser()?.signOut();
    setUser(null);
    setIdToken(null);
  };

  return (
    <UserContext.Provider
      value={{ user, isLoading, isAuthenticated: !!user, idToken, login, register, confirmSignUp, logout }}
    >
      {children}
    </UserContext.Provider>
  );
};
